import { useState } from "react";

import { type AxiosError } from "axios";

import { useApiContext } from "../../../../api/context";
import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { useErrorSnackbar, useSuccessSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";

interface ConnectionCredentials {
  clientId: string;
  clientSecret: string;
}

interface ConnectionResult {
  success: boolean;
  message: string;
}

export const useTestConnection = () => {
  const customerId = useCustomerId();
  const api = useApiContext();
  const showErrorSnackbar = useErrorSnackbar();
  const showSuccessSnackbar = useSuccessSnackbar();

  const testConnection = async (orgId: string, credentials: ConnectionCredentials): Promise<ConnectionResult> => {
    try {
      await api.post(`/v1/customers/${customerId}/integrations/wiz/test-connection`, {
        clientId: credentials.clientId,
        clientSecret: credentials.clientSecret,
        orgId,
      });

      showSuccessSnackbar("Connection test successful");
      return {
        success: true,
        message: "Your credentials are correct. Click on finish to onboard your organization.",
      };
    } catch {
      showErrorSnackbar("Connection test failed");
      return {
        success: false,
        message: "Error testing connection. Check the credentials and try again.",
      };
    }
  };

  return {
    testConnection,
  };
};

export const useOnboardWiz = () => {
  const customerId = useCustomerId();
  const api = useApiContext();
  const showErrorSnackbar = useErrorSnackbar();
  const showSuccessSnackbar = useSuccessSnackbar();
  const [onboarding, setOnboarding] = useState(false);

  const onboard = async (
    orgId: string,
    orgName: string,
    credentials: ConnectionCredentials
  ): Promise<ConnectionResult> => {
    setOnboarding(true);
    try {
      await api.post(`/v1/customers/${customerId}/integrations/wiz/onboard-customer`, {
        clientId: credentials.clientId,
        clientSecret: credentials.clientSecret,
        orgId,
        orgName,
      });

      showSuccessSnackbar("Onboarding successful");
      return {
        success: true,
        message: "You have successfully connected your Wiz account and we will start importing your data.",
      };
    } catch (error) {
      const apiError = error as AxiosError<{ error?: string }>;
      const statusCode = apiError.response?.status;

      if (statusCode === 409) {
        showErrorSnackbar("Organization already onboarded");
        return {
          success: false,
          message: "Organization already onboarded",
        };
      }

      showErrorSnackbar("Error during onboarding. Please try again.");
      return {
        success: false,
        message: "Error during onboarding. Please try again.",
      };
    } finally {
      setOnboarding(false);
    }
  };

  return {
    onboard,
    onboarding,
  };
};
